import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, HeartPulse } from "lucide-react";
import { orpc } from "@/frontend/orpc-client";
import { DEMOPASS, WRONG_PASSWORD_MESSAGE } from "@/frontend/constants/synthetic-hospital";
import { UploadPatientDialog } from "./UploadPatientDialog";
import { LoginStep } from "./LoginStep";
import { AuthorizeStep } from "./AuthorizeStep";
import type { AuthorizeSubmitting, LoginFlowStep, SamplePatient } from "@/frontend/types/synthetic-hospital";

/**
 * Two-step in-portal Synthetic Hospital login. Reached by clicking Connect on
 * the Synthetic Hospital tile in the patient portal. Session-only:
 *   Step 1 (login)      — pick a sample patient from the dropdown, keep `demopass`, hit Login.
 *   Step 2 (authorize)  — Approve creates a `connection` row via `orpc.syntheticHospital.linkSamplePatient` and navigates back to the picker; Deny navigates back with no side effect.
 * The password check is client-side only; the real auth on both oRPC calls is the patient session cookie.
 */
export function SyntheticHospitalLoginView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<LoginFlowStep>("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState(DEMOPASS);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [submitting, setSubmitting] = useState<AuthorizeSubmitting>(null);

  const samplePatientsQuery = useQuery(orpc.syntheticHospital.listSamplePatients.queryOptions({ input: {} }));
  const linkMutation = useMutation(orpc.syntheticHospital.linkSamplePatient.mutationOptions());

  const samplePatients: SamplePatient[] = samplePatientsQuery.data ?? [];
  const loadingSamplePatients = samplePatientsQuery.isLoading;

  // seed the dropdown with the first sample patient on first list-resolve; the assignment is a no-op after any user pick
  if (!username && samplePatients.length > 0) {
    setUsername(samplePatients[0]!.username);
  }

  /** Handles the Login submit — validates the client-side password then advances to the authorize step. */
  function onLogin(event: React.FormEvent) {
    event.preventDefault();
    if (!username) return;
    if (password !== DEMOPASS) {
      setPasswordError(WRONG_PASSWORD_MESSAGE);
      return;
    }
    setPasswordError(null);
    setStep("authorize");
  }

  /** Handles the Approve click — creates the connection via `linkSamplePatient`, refreshes downstream queries, and navigates back to the portal. */
  async function onApprove() {
    if (!username) return;
    setSubmitting("approve");
    try {
      await linkMutation.mutateAsync({ username });
      queryClient.invalidateQueries({ queryKey: orpc.accounts.list.key() });
      queryClient.invalidateQueries({ queryKey: orpc.syntheticHospital.listSamplePatients.key() });
      navigate({ to: "/" });
    } catch (err) {
      console.error("Failed to link sample patient:", err);
      setSubmitting(null);
    }
  }

  /** Handles the Deny click — no side effects, just returns to the picker. */
  function onDeny() {
    setSubmitting("deny");
    navigate({ to: "/" });
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8">
      {step === "authorize" && (
        <div className="w-full max-w-md mb-3">
          <button
            type="button"
            onClick={() => setStep("login")}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Back
          </button>
        </div>
      )}
      <header className="mb-6 flex flex-col items-center gap-1 text-center">
        <HeartPulse className="size-7 text-primary" />
        <h1 className="text-2xl font-semibold leading-tight">Synthetic Hospital</h1>
        <p className="text-sm text-muted-foreground max-w-md">
          Sign in as a preloaded sample patient, or add your own FHIR Bundle.
        </p>
      </header>

      <div className="w-full max-w-md">
        {step === "login" ? (
          <LoginStep
            samplePatients={samplePatients}
            loading={loadingSamplePatients}
            username={username}
            onUsernameChange={setUsername}
            password={password}
            onPasswordChange={setPassword}
            passwordError={passwordError}
            onSubmit={onLogin}
            onOpenUpload={() => setUploadOpen(true)}
          />
        ) : (
          <AuthorizeStep
            samplePatient={samplePatients.find((sp) => sp.username === username) ?? null}
            submitting={submitting}
            onApprove={onApprove}
            onDeny={onDeny}
          />
        )}
      </div>

      <UploadPatientDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onPick={({ username: pickedUsername }) => {
          setUsername(pickedUsername);
          setStep("authorize");
          setUploadOpen(false);
        }}
      />
    </div>
  );
}
